import React from 'react';
import { useTableContext } from '../../context';
import { TableHeader } from '../headers/table-header';
import { TableHeaderProps } from '../../types';

/**
 * Props for TableHeaderSection component
 */
export interface TableHeaderSectionProps extends Partial<TableHeaderProps> {
  /** Override the default header props */
  headerProps?: Partial<TableHeaderProps>;
}

/**
 * TableHeaderSection component - Renders the table header section
 *
 * This component is responsible for rendering the search input and column visibility
 * controls when the header is enabled. It uses the table context to access shared state.
 *
 * @template T - The type of data rows in the table
 */
export function TableHeaderSection<T extends Record<string, unknown>>({
  headerProps,
  ...props
}: TableHeaderSectionProps) {
  const { showHeader, searchValue, setSearchValue, columns, columnVisibility } =
    useTableContext<T>();

  if (!showHeader) {
    return null;
  }

  return (
    <TableHeader
      onSearch={setSearchValue}
      searchValue={searchValue}
      columns={columns.map((c) => ({
        id: String(c.id),
        label: c.label,
        visible: columnVisibility.visibility[String(c.id)] ?? !c.hidden,
      }))}
      onColumnVisibilityChange={columnVisibility.onColumnVisibilityChange}
      {...props}
      {...headerProps}
    />
  );
}

TableHeaderSection.displayName = 'TableHeaderSection';
